import { useState } from "react";
import { api, formatApiError } from "@/lib/kfmApi";
import { useTrackedLinks, trackedIntent, trackedCopyLink, trackedCaption, trackedEmailHref } from "@/lib/utm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Copy, Check, Loader2, Twitter, Facebook, MessageCircle, Mail, Linkedin, Instagram, Share2, Eye } from "lucide-react";
import { toast } from "sonner";

// A generic "create a public link + share to social" dialog, reused for sharing a single
// study section and a whole Go Deeper conversation. The parent controls the trigger.
//   basePath — REST base supporting GET/POST/DELETE and returning { shared, url, read_url, image_url, views }
export default function SharePublicDialog({
  basePath,
  renderTrigger,
  title = "Share",
  description = "",
  hook = "",
  campaign = "share",
  ctaLabel = "Read it on OpenWord AI:",
  emailSubject = "A study from OpenWord AI",
  previewTitle = "OpenWord AI",
  testid = "share",
  showHighlightsToggle = false,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [share, setShare] = useState(null);
  const [copied, setCopied] = useState(false);
  const [savingOpt, setSavingOpt] = useState(false);

  const ensureShare = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(basePath);
      if (data.shared) setShare(data);
      else {
        const res = await api.post(basePath);
        setShare(res.data);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't create a share link");
    }
    setLoading(false);
  };

  const toggleHighlights = async (value) => {
    setSavingOpt(true);
    try {
      const { data } = await api.post(basePath, { include_highlights: value });
      setShare(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update this");
    }
    setSavingOpt(false);
  };

  const onOpen = () => {
    setOpen(true);
    if (!share) ensureShare();
  };

  const link = share?.url || "";
  const tracked = useTrackedLinks(link, campaign);
  const copyTarget = trackedCopyLink(tracked, link, campaign);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(copyTarget);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy — select and copy the link manually");
    }
  };

  const copyInstagram = async () => {
    try {
      await navigator.clipboard.writeText(trackedCaption(tracked, link, hook, campaign, ctaLabel));
      toast.success("Caption + link copied — paste it into your Instagram post or story");
    } catch {
      toast.error("Couldn't copy the caption");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: previewTitle, text: hook, url: copyTarget }); } catch { /* dismissed */ }
    } else copy();
  };

  const emailHref = () => trackedEmailHref(tracked, link, [hook, ctaLabel], emailSubject, campaign);

  const targets = [
    { id: "x", label: "Share on X", Icon: Twitter, href: trackedIntent("x", tracked, link, hook, campaign, ctaLabel) },
    { id: "facebook", label: "Share on Facebook", Icon: Facebook, href: trackedIntent("fb", tracked, link, hook, campaign, ctaLabel) },
    { id: "whatsapp", label: "Share on WhatsApp", Icon: MessageCircle, href: trackedIntent("wa", tracked, link, hook, campaign, ctaLabel) },
    { id: "linkedin", label: "Share on LinkedIn", Icon: Linkedin, href: trackedIntent("li", tracked, link, hook, campaign, ctaLabel) },
  ];

  let previewDomain = "openwordai.com";
  try { previewDomain = new URL(share?.read_url || link).host || previewDomain; } catch { /* noop */ }

  const unshare = async () => {
    try {
      await api.delete(basePath);
      setShare(null);
      setOpen(false);
      toast.success("This link has been turned off");
    } catch {
      toast.error("Couldn't stop sharing");
    }
  };

  return (
    <>
      {renderTrigger(onOpen)}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${testid}-dialog`} className="bg-[#0E131F] sm:max-w-md rounded-2xl max-h-[90dvh] overflow-y-auto w-[calc(100vw-2rem)] p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-2xl text-white">{title}</DialogTitle>
            {description && <DialogDescription className="text-sm text-slate-400">{description}</DialogDescription>}
          </DialogHeader>

          {loading || !share ? (
            <div className="py-12 flex flex-col items-center gap-3 text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]" strokeWidth={1.5} />
              <span className="text-sm">Preparing a shareable link…</span>
            </div>
          ) : (
            <div className="space-y-5">
              {showHighlightsToggle && (
                <label
                  data-testid={`${testid}-opt-include_highlights`}
                  className="flex items-start gap-3 rounded-xl border border-[#29344D] px-3 py-2.5 cursor-pointer hover:bg-[#0B0F16] transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={!!share.include_highlights}
                    disabled={savingOpt}
                    onChange={(e) => toggleHighlights(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-[#3A4763] text-[#00E5FF] focus:ring-[#00E5FF]"
                  />
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-slate-200">My highlights & notes</span>
                    <span className="block text-xs text-slate-500">Text you highlighted and any handwritten notes you attached</span>
                  </span>
                </label>
              )}

              {typeof share.views === "number" && (
                <div data-testid={`${testid}-views`} className="flex items-center gap-2 text-sm text-[#00E5FF] bg-[#10222A]/60 rounded-xl px-3 py-2">
                  <Eye className="w-4 h-4" strokeWidth={1.75} />
                  {share.views === 0 ? (
                    <span>Not opened yet — share the link to see it here.</span>
                  ) : (
                    <span>Opened <strong>{share.views}</strong> {share.views === 1 ? "time" : "times"}
                      {share.last_viewed_at ? ` · last opened ${new Date(share.last_viewed_at).toLocaleDateString()}` : ""}</span>
                  )}
                </div>
              )}

              {/* How the link will look when posted */}
              <div data-testid={`${testid}-preview`} className="rounded-xl border border-[#29344D] overflow-hidden">
                <div className="w-full aspect-[1.91/1] bg-[#141B29]">
                  {share.image_url && (
                    <img src={share.image_url} alt="Link preview" className="w-full h-full object-cover" data-testid={`${testid}-preview-img`} />
                  )}
                </div>
                <div className="bg-[#0E131F] px-3 py-2.5">
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 truncate">{previewDomain}</div>
                  <div className="text-sm font-semibold text-white leading-snug line-clamp-2">{previewTitle}</div>
                  {hook && <div className="text-xs text-slate-400 leading-snug line-clamp-2 mt-0.5">{hook}</div>}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  data-testid={`${testid}-link`}
                  readOnly
                  value={link}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 h-11 rounded-xl border border-[#29344D] bg-[#0B0F16] px-3 text-sm text-slate-400 truncate focus:outline-none"
                />
                <button
                  data-testid={`${testid}-copy`}
                  onClick={copy}
                  className="h-11 w-11 shrink-0 rounded-xl bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] flex items-center justify-center transition-colors"
                  title="Copy link"
                >
                  {copied ? <Check className="w-5 h-5" strokeWidth={1.5} /> : <Copy className="w-5 h-5" strokeWidth={1.5} />}
                </button>
              </div>

              <div className="space-y-2">
                <button
                  data-testid={`${testid}-native`}
                  onClick={nativeShare}
                  className="w-full h-11 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                >
                  <Share2 className="w-4 h-4" strokeWidth={1.5} /> Share…
                </button>
                <div className="flex items-center flex-wrap gap-2">
                  {targets.map((t) => (
                    <a
                      key={t.id}
                      data-testid={`${testid}-${t.id}`}
                      href={t.href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={t.label}
                      title={t.label}
                      className="w-11 h-11 shrink-0 flex items-center justify-center rounded-full bg-[#10222A] text-[#00E5FF] hover:bg-[#10222A] transition-colors"
                    >
                      <t.Icon className="w-4 h-4" strokeWidth={1.5} />
                    </a>
                  ))}
                  <button
                    data-testid={`${testid}-instagram`}
                    onClick={copyInstagram}
                    aria-label="Copy caption for Instagram"
                    title="Instagram has no direct web share — copy the caption + link to paste"
                    className="w-11 h-11 shrink-0 flex items-center justify-center rounded-full bg-[#10222A] text-[#00E5FF] hover:bg-[#10222A] transition-colors"
                  >
                    <Instagram className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  <a
                    data-testid={`${testid}-email`}
                    href={emailHref()}
                    aria-label="Share by email"
                    title="Share by email"
                    className="w-11 h-11 shrink-0 flex items-center justify-center rounded-full bg-[#10222A] text-[#00E5FF] hover:bg-[#10222A] transition-colors"
                  >
                    <Mail className="w-4 h-4" strokeWidth={1.5} />
                  </a>
                </div>
              </div>

              <button
                data-testid={`${testid}-unshare`}
                onClick={unshare}
                className="w-full text-sm text-slate-500 hover:text-red-500 transition-colors"
              >
                Turn off this link
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
